/**
 * Boss — the level-end encounter.
 *
 * Two phases, in the Space Impact tradition:
 *   Phase 1 — formation hold: the boss drifts left-and-right across the
 *             screen at a moderate speed, firing slow projectiles every
 *             few seconds.
 *   Phase 2 — attack run: once HP drops below 50 %, the boss charges at
 *             the player from the right edge of the screen.
 *
 * Motion is driven by Arcade Physics (`setVelocityX`) instead of direct
 * property mutation. Phaser tweens that touch `.x` from outside (the
 * spawn-in animation) are KEPT but apply only to the body velocity; after
 * the tween, the body keeps moving on its own. The previous version
 * mutated `this.x` directly inside `update()` while a tween was running,
 * which produced undefined NaN positions and froze the scene.
 *
 * Eyes: each boss has two white googly eyes with black pupils. The pupils
 * track the player position every frame, giving the waste monster life.
 */
import Phaser from "phaser";
import { GAME_WIDTH } from "../Game";
import { type PhaseDef } from "../config/phases";
import { EnemyKind } from "./Enemy";

export class Boss extends Phaser.Physics.Arcade.Sprite {
  public hp = 60;
  public maxHp = 60;
  public phase: 1 | 2 = 1;
  public scoreValue = 1000;
  private direction = -1; // start moving left
  private enteredStage = false;
  private lastShotAt = 0;
  private bornAt = 0;
  private phaseDef: PhaseDef;
  /** Tracks which HP thresholds have already triggered a shed event. */
  private shedThresholds = [0.75, 0.50, 0.25];
  private shedTriggered: boolean[] = [false, false, false];
  private lastMinionAt = 0;

  // ---- Boss eyes & angry eyebrows that track the player ----
  private eyeLeft!: Phaser.GameObjects.Graphics;
  private eyeRight!: Phaser.GameObjects.Graphics;
  private pupilLeft!: Phaser.GameObjects.Graphics;
  private pupilRight!: Phaser.GameObjects.Graphics;
  private browLeft!: Phaser.GameObjects.Graphics;
  private browRight!: Phaser.GameObjects.Graphics;

  /** Radius of the white sclera circles. */
  private static readonly EYE_R = 18;
  /** Radius of the black pupil circles. */
  private static readonly PUPIL_R = 8;
  /** How far the pupil can slide from the eye centre (pixels). */
  private static readonly PUPIL_MAX_OFFSET = 7;

  constructor(scene: Phaser.Scene, x: number, y: number, phaseDef: PhaseDef) {
    const textureKey = Boss.getOrGenerateCompositeTexture(scene, phaseDef);

    super(scene, x, y, textureKey);
    this.phaseDef = phaseDef;
    scene.add.existing(this);
    scene.physics.add.existing(this);
    const body = this.body as Phaser.Physics.Arcade.Body;
    this.setScale(0.44);
    // Boss hitbox is 30% smaller than the 480x540 generated texture to be fair
    body.setSize(480 * 0.7, 540 * 0.7);
    body.setOffset(480 * 0.15, 540 * 0.15);
    // The boss is dynamic so we can move it via velocity; setting static
    // would block both tween-in and the future phase changes.
    this.bornAt = scene.time.now;
    body.setAllowGravity(false);
    body.moves = true;
    body.setVelocity(0, 0);

    this.createEyes();
  }

  // ---- Eyes & Eyebrows ---------------------------------------------------

  private createEyes(): void {
    const scene = this.scene;
    const depth = this.depth + 1;

    // Sclera — white filled circles with a thin black outline
    this.eyeLeft = scene.add.graphics();
    this.eyeRight = scene.add.graphics();
    for (const g of [this.eyeLeft, this.eyeRight]) {
      g.fillStyle(0xffffff, 1);
      g.fillCircle(0, 0, Boss.EYE_R);
      g.lineStyle(2, 0x000000, 1);
      g.strokeCircle(0, 0, Boss.EYE_R);
      g.setDepth(depth);
    }

    // Pupils — solid black circles
    this.pupilLeft = scene.add.graphics();
    this.pupilRight = scene.add.graphics();
    for (const g of [this.pupilLeft, this.pupilRight]) {
      g.fillStyle(0x000000, 1);
      g.fillCircle(0, 0, Boss.PUPIL_R);
      g.setDepth(depth + 1);
    }

    // Angry eyebrows — thick black angled lines pointing down towards center
    this.browLeft = scene.add.graphics();
    this.browRight = scene.add.graphics();

    // Left eyebrow (\ slope)
    this.browLeft.lineStyle(5, 0x000000, 1);
    this.browLeft.beginPath();
    this.browLeft.moveTo(-22, -12);
    this.browLeft.lineTo(14, 4);
    this.browLeft.strokePath();

    // Right eyebrow (/ slope)
    this.browRight.lineStyle(5, 0x000000, 1);
    this.browRight.beginPath();
    this.browRight.moveTo(22, -12);
    this.browRight.lineTo(-14, 4);
    this.browRight.strokePath();
  }

  /**
   * Reposition the eyes and eyebrows on the boss body and rotate pupils
   * toward the player. Call every frame from GameScene after boss.update().
   */
  public updateEyes(targetX: number, targetY: number): void {
    if (!this.eyeLeft || !this.active) return;

    // Eye positions — roughly upper-third, spread horizontally.
    const hw = this.displayWidth * 0.22;
    const ey = this.y - this.displayHeight * 0.15;

    const lx = this.x - hw;
    const rx = this.x + hw;

    this.eyeLeft.setPosition(lx, ey);
    this.eyeRight.setPosition(rx, ey);
    this.browLeft.setPosition(lx, ey - 10);
    this.browRight.setPosition(rx, ey - 10);

    // Compute pupil offsets toward the target
    for (const [eyeG, pupilG] of [
      [this.eyeLeft, this.pupilLeft],
      [this.eyeRight, this.pupilRight],
    ] as [Phaser.GameObjects.Graphics, Phaser.GameObjects.Graphics][]) {
      const dx = targetX - eyeG.x;
      const dy = targetY - eyeG.y;
      const dist = Math.max(1, Math.hypot(dx, dy));
      const ox = (dx / dist) * Boss.PUPIL_MAX_OFFSET;
      const oy = (dy / dist) * Boss.PUPIL_MAX_OFFSET;
      pupilG.setPosition(eyeG.x + ox, eyeG.y + oy);
    }

    // Ensure eyes, pupils, and eyebrows render on top of the boss sprite
    const baseDepth = this.depth > 0 ? this.depth : 12;
    this.eyeLeft.setDepth(baseDepth + 1);
    this.eyeRight.setDepth(baseDepth + 1);
    this.pupilLeft.setDepth(baseDepth + 2);
    this.pupilRight.setDepth(baseDepth + 2);
    this.browLeft.setDepth(baseDepth + 3);
    this.browRight.setDepth(baseDepth + 3);

    // Match boss visibility
    const v = this.visible;
    this.eyeLeft.setVisible(v);
    this.eyeRight.setVisible(v);
    this.pupilLeft.setVisible(v);
    this.pupilRight.setVisible(v);
    this.browLeft.setVisible(v);
    this.browRight.setVisible(v);
  }

  /** Remove eye and eyebrow graphics when the boss is destroyed. */
  public destroyEyes(): void {
    this.eyeLeft?.destroy();
    this.eyeRight?.destroy();
    this.pupilLeft?.destroy();
    this.pupilRight?.destroy();
    this.browLeft?.destroy();
    this.browRight?.destroy();
  }

  // ---- Composite texture --------------------------------------------------

  public static getOrGenerateCompositeTexture(scene: Phaser.Scene, phaseDef: PhaseDef): string {
    const key = `boss_composite_${phaseDef.id}`;
    if (scene.textures.exists(key)) return key;

    // We must generate it
    const rt = scene.add.renderTexture(0, 0, 480, 540);

    // Get the textures for this phase's enemy kind.
    // If it's Infinite Fusion (phase 13), mix all kinds!
    let kindsToMix = [phaseDef.enemyKind];
    if (phaseDef.id >= 13) {
      kindsToMix = ["cardboard", "can", "plastic", "biowaste", "toxic", "radioactive", "chemicals"] as EnemyKind[];
    }

    // Draw ~60 random sprites into the render texture
    for (let i = 0; i < 60; i++) {
      const k = kindsToMix[Math.floor(Math.random() * kindsToMix.length)];
      const variants: string[] = [];
      for (let j = 1; j <= 40; j++) {
          const t = `enemy_${k}_${j}`;
          if (scene.textures.exists(t)) {
              variants.push(t);
          }
      }
      
      const tex = variants.length > 0 ? variants[Math.floor(Math.random() * variants.length)] : "enemy_cardboard_1";
      
      const tempImage = scene.make.image({ x: 0, y: 0, key: tex, add: false });
      tempImage.setAngle(Math.random() * 360);
      const scale = 0.5 + Math.random() * 1.5;
      tempImage.setScale(scale);
      
      const dw = tempImage.displayWidth || (tempImage.width * scale * 1.5);
      const dh = tempImage.displayHeight || (tempImage.height * scale * 1.5);
      
      const rx = (dw / 2) + Math.random() * Math.max(0, 480 - dw);
      const ry = (dh / 2) + Math.random() * Math.max(0, 540 - dh);
      
      tempImage.setPosition(rx, ry);
      rt.draw(tempImage);
      tempImage.destroy();
    }

    rt.saveTexture(key);
    rt.destroy();
    return key;
  }

  public entered(): boolean {
    return this.enteredStage;
  }

  public markEntered(): void {
    this.enteredStage = true;
  }

  public update(now: number): void {
    if (!this.enteredStage) return;

    const body = this.body as Phaser.Physics.Arcade.Body;
    const isSpeed = this.phaseDef.bossAbility === "SPEED";
    const speedMult = isSpeed ? 1.5 : 1.0;

    if (this.phase === 1) {
      const driftSpeed = (80 + (this.phaseDef.id * 5)) * speedMult;
      body.setVelocityX(this.direction * driftSpeed);
      if (this.x > GAME_WIDTH - 120) this.direction = -1;
      if (this.x < GAME_WIDTH * 0.7) this.direction = 1;

      const swayHz = Math.max(200, 600 - this.phaseDef.id * 20) / speedMult;
      body.setVelocityY(Math.cos((now - this.bornAt) / swayHz) * 12 * speedMult);
      
      if (this.hp <= this.maxHp / 2) this.phase = 2;
    } else {
      const targetX = GAME_WIDTH * 0.7;
      const dx = targetX - this.x;
      const chaseSpeedCap = (100 + this.phaseDef.id * 10) * speedMult;
      body.setVelocityX(Math.sign(dx) * Math.min(Math.abs(dx) * 2, chaseSpeedCap));
      
      const swayHz = Math.max(150, 400 - this.phaseDef.id * 20) / speedMult;
      body.setVelocityY(Math.cos((now - this.bornAt) / swayHz) * 24 * speedMult);
      
      const spawnRate = Math.max(500, 3000 - this.phaseDef.id * 200);
      if (now - this.lastMinionAt > spawnRate) {
        this.lastMinionAt = now;
        this.emit('spawnMinion');
      }
    }
  }

  public damage(amount: number): boolean {
    this.hp -= amount;
    this.refreshDamageState();
    return this.hp <= 0;
  }

  /**
   * Visual feedback: shrink as HP drops, emit 'shedPart' at thresholds.
   */
  public refreshDamageState(): void {
    const ratio = Math.max(0, this.hp / this.maxHp);
    const s = 0.44 * (0.4 + 0.6 * ratio);
    this.setScale(s);

    for (let i = 0; i < this.shedThresholds.length; i++) {
      if (!this.shedTriggered[i] && ratio <= this.shedThresholds[i]) {
        this.shedTriggered[i] = true;
        this.emit('shedPart', this.x, this.y);
      }
    }
  }

  /**
   * Pixel-shatter death effect. Spawns white pixel squares that fly out
   * and fade. Returns after the last particle finishes.
   */
  public deathShatter(): void {
    const scene = this.scene;
    const cx = this.x;
    const cy = this.y;
    const count = 25;
    for (let i = 0; i < count; i++) {
      const size = Phaser.Math.Between(3, 8);
      const g = scene.add.graphics();
      g.fillStyle(0xffffff, 1);
      g.fillRect(-size / 2, -size / 2, size, size);
      g.setPosition(
        cx + Phaser.Math.Between(-40, 40),
        cy + Phaser.Math.Between(-40, 40)
      );
      g.setDepth(20);
      const vx = Phaser.Math.Between(-300, 300);
      const vy = Phaser.Math.Between(-300, 300);
      scene.tweens.add({
        targets: g,
        x: g.x + vx * 0.6,
        y: g.y + vy * 0.6,
        alpha: 0,
        duration: Phaser.Math.Between(400, 800),
        ease: 'Cubic.easeOut',
        onComplete: () => g.destroy(),
      });
    }
  }

  public canShoot(now: number): boolean {
    if (!this.enteredStage) return false;
    let cooldown = Math.max(300, 1500 - this.phaseDef.id * 90);
    if (this.phaseDef.bossAbility === "FIRE_RATE") {
      cooldown *= 0.6;
    }
    return now - this.lastShotAt > cooldown;
  }

  public markShot(now: number): void {
    this.lastShotAt = now;
  }

  /** Aim the boss's projectile toward the player. */
  public aimAt(targetX: number, targetY: number): { vx: number; vy: number } {
    const dx = targetX - this.x;
    const dy = targetY - this.y;
    const len = Math.max(1, Math.hypot(dx, dy));
    const speed = 200;
    return { vx: (dx / len) * speed, vy: (dy / len) * speed };
  }
}
